/**
 * @module app.shared
 */

import { hasProperty, resolveProperty } from 'app/shared/lib/objects';

/**
 * Use when binding Essette models with Aurelia (matcher.bind)
 */

/**
 * Function that checks for a match between two items, based on specific criteria, and returns a `true` if the items match.
 */
export type MatcherFunction = (a: any, b: any) => boolean;

/**
 * Check if two objects or primitives match based on a set of properties
 * @param a First match parameter
 * @param b Second match parameter
 * @param matchProperties An array of object properties, that should exist on both `a` and `b`, that are used to determine a match.
 * @returns `true` if `a` and `b` match
 */
export function matcher(a: any, b: any, ...matchProperties: string[]): boolean {
    // INFO [MD] Weird behavior around falsy values, unlikely problem.
    if((a || null) === (b || null)) {
        return true;
    }
    if(a === null || a === undefined || b === null || b === undefined) {
        return false;
    }
    if(a instanceof Object && b instanceof Object && matchProperties.length > 0) {
        return matchProperties.every((property) =>
            hasProperty(a, property) && hasProperty(b, property) && resolveProperty(a, property) === resolveProperty(b, property)
        );
    }
    return a === b;
}

/**
 * Create a function to check if two objects or primitives match based on a set of properties
 * @param matchProperties An array of object properties, that should exist on both `a` and `b`, that are used to determine a match.
 * @returns A function of type `MatcherFunction`
 */
export function getMatcherFunction(...matchProperties: string[]): MatcherFunction {
    return function(a: any, b: any): boolean {
        if((a || null) === (b || null)) {
            return true;
        }
        if(a === null || a === undefined || b === null || b === undefined) {
            return false;
        }
        if(a instanceof Object && b instanceof Object && matchProperties.length > 0) {
            return matchProperties.every((property) =>
                hasProperty(a, property) && hasProperty(b, property) && resolveProperty(a, property) === resolveProperty(b, property)
            );
        }
        return a === b;
    }
}

/**
 * Check if two models match based on their `id` properties
 * @param a First match parameter
 * @param b Second match parameter
 * @returns A boolean result based on whether `a` and `b` match (match = `true`)
 */
export function modelIdMatcher(a: any, b: any): boolean {
    return matcher(a, b, 'id');
}

/**
 * Check if two models match based on their `$display` properties
 * @param a First match parameter
 * @param b Second match parameter
 * @returns A boolean result based on whether `a` and `b` match (match = `true`)
 */
export function modelDisplayMatcher(a: any, b: any): boolean {
    return matcher(a, b, '$display');
}
